import { Component, effect, ElementRef, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Country } from '../../shared/../postal/country.model';
import { PostalService } from '../../shared/../postal/postal.service';
import { CountriesComponentState } from '../../shared/state-interface';
import { StateManager } from '../../shared/state-manager.class';

@Component({
    selector: 'app-countries-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule
    ],
    templateUrl: 'countries.component.html',
    styleUrl: 'countries.component.css'
})
export class CountriesComponent {

    @ViewChild("input") userInput: ElementRef<HTMLInputElement> | undefined;
    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private postalService: PostalService = inject(PostalService);

    public static defaultCountry = "USA";
    private static _countriesError = "Get Countries error:";

    protected country: FormControl = new FormControl<string | Country>("");
    private _required: boolean = false;
    protected filteredCountries: Country[] = [];

    private _countries: WritableSignal<Country[]> = signal([]);
    private _countryCode: WritableSignal<string> = signal("");

    public constructor() {
        const effectRef = effect(() => {
            if (this._countries().length > 0 && this._countryCode() !== "") {
                for (const country of this._countries()) {
                    if (country.alpha_3_code == this._countryCode()) {
                        this.country.setValue(country);
                        break;
                    }
                }
                // Only need/expect to run this effect one time
                // once all conditions are met. Not sure that it
                // is necessary to destroy but doing so to be on
                // the safe side relative to avoiding memory leak.
                effectRef.destroy();
            }
        },
            { manualCleanup: true }
        );
    }

    public getState(): CountriesComponentState {
        const state: CountriesComponentState = {} as CountriesComponentState;
        state.countryControl = this.country;
        state.required = this._required;
        return state;
    }

    public restoreState(state: CountriesComponentState) {
        this.country = state.countryControl;
        this._required = state.required;
        this._countryCode.set(this.country.value instanceof Country ? this.country.value.alpha_3_code : "");
        this._countries.set(this.stateManager.countries!);
    }

    public init() {
        if (this._countries().length == 0) {
            const countries = this.stateManager.countries;

            if (countries == undefined) {
                this.country.setValue("Loading...");
                this.loadCountries();
            } else {
                this._countries.set(countries);
            }
        }
    }

    public get required(): boolean {
        return this._required;
    }

    public set required(value: boolean) {
        this._required = value;
    }

    public get selectedCountry(): string {
        return this.country.value instanceof Country ? this.country.value.alpha_3_code : "";
    }

    public set countryCode(value: string | undefined) {
        if (value != undefined) {
            this._countryCode.set(value);
            this.country.setValue(value);
        }
    }

    // Note: Must use "arrow" function declaration here
    // in order to have access to 'this'. If using normal
    // function declaration, then 'this' will refer to the
    // mat-select object.
    public displayValueFunc = (country: Country) => {
        if (country instanceof Country) {
            return `${country.en_short_name}`;
        } else {
            return country;
        }
    }

    public filter(): void {
        if (this.userInput != undefined && this._countries().length > 0) {
            const filterValue = this.userInput.nativeElement.value.toLowerCase();
            this.filteredCountries = this._countries().filter(
                country => `${country.alpha_3_code} ${country.en_short_name}`.toLowerCase().includes(filterValue)
            );
        }
    }

    protected async loadCountries() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.postalService.getCountries(await getIdToken(currentUser));

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-countries"] == "success") {
                        this._countries.set(Country.toCountries(json["countries"]));
                        this.stateManager.countries = this._countries();
                        this.country.setValue("");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${CountriesComponent._countriesError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //},2500);
            })
        }
    }
}